// Domänenmodell von Dispositio (Story-Modell). Reine Typen, keine Abhängigkeiten, kein I/O.

export type FieldType = 'text' | 'longtext' | 'number' | 'date' | 'image';
/** Art eines Felds einer Klasse: zusätzlich `ref`, ein Verweis auf einen Bibliothekseintrag (Wert = Entity-ID), z. B. die Perspektive einer Szene; nur in vordefinierten Klassen. */
export type FieldDefType = FieldType | 'ref';
export type FieldValue = string | number | null;

export interface FieldDef {
  key: string;
  label: string;
  type: FieldDefType;
  /** Untertitel: kurze Erklärung, was in das Feld gehört (steht unter der Beschriftung) */
  help?: string;
  /** Beispieltext im leeren Feld */
  placeholder?: string;
  /** Feste Auswahl (z. B. männlich/weiblich/sonst): höchstens eine, keine Wahl = null */
  options?: string[];
  /** Nur bei `ref`: Name der Klasse, deren Einträge gewählt werden können (z. B. „Person“) */
  refType?: string;
}

/**
 * Kategorie eines Node-Typs.
 *  - story:        Wurzel, genau eine je Projekt
 *  - foundational: Singleton (Prämisse, Welt), nur in der Seitenleiste, nie auf dem Canvas
 *  - timeline / event / period: Zeitstrahl und seine Elemente
 *  - object / place: „Bibliothekseinträge“ (Entities): intern einmalig, auf dem Canvas beliebig oft als Instanz
 *  - normal: frei kopierbare Nodes (z. B. Handlung)
 *  - manuscript / section / chapter / scene: Erzählebene (Manuskript → Abschnitt → Kapitel → Szene; Abschnitte sind optional), nie auf
 *    der Planungsfläche
 */
export type TypeCategory = 'story' | 'foundational' | 'timeline' | 'event' | 'period' | 'object' | 'place' | 'normal' | 'manuscript' | 'section' | 'chapter' | 'scene';

export interface NodeType {
  id: string;
  name: string;
  category: TypeCategory;
  builtin: boolean;
  /** Typfarbe (erste der zwei Markierungen) */
  color: string;
  fields: FieldDef[];
  /** Feld, dessen Wert der Titel des Nodes ist */
  titleKey: string;
  /** Feld, dessen Wert als Hover-Nachricht erscheint */
  hoverKey: string | null;
  /** Bildfeld, das als Titelbild in der Box erscheint */
  imageKey: string | null;
  /** Standardbild der Klasse (Asset-ID): erscheint, wenn ein Node kein eigenes Titelbild hat */
  placeholderId: string | null;
  /** Nodes dieses Typs haben Textdokumente (Zusammenfassung, Draft, weitere) */
  textual: boolean;
  sort: number;
}

/** Bibliothekseintrag mit statischen Feldern; existiert intern genau einmal, auf dem Canvas erscheinen Instanzen. */
export interface Entity {
  id: string;
  typeId: string;
  fields: Record<string, FieldValue>;
  /** Kurzname zum Verweisen im Text („hero“ für „@hero“), je Projekt eindeutig, Kleinbuchstaben */
  refKey: string;
  /**
   * Vorkommen: Wörter und kurze Wendungen, mit denen das Element im Text vorkommt („Clara“, „die Kapitänin“). Entstehen beim
   * Verknüpfen von selbst und lassen sich von Hand ergänzen; zusammen mit dem Namen findet Dispositio damit nicht verknüpfte Stellen.
   */
  terms?: string[];
  /**
   * Die Vorkommen, die beim Verknüpfen von selbst gelernt wurden (Teil von `terms`, fehlend = keine). Verschwindet ihre letzte
   * Verknüpfung mit dem Element (Strg+Z, ✕ am Verweis, Text geändert), werden sie wieder verlernt; von Hand ergänzte bleiben.
   */
  learned?: string[];
  /** Nur ältere Daten: weitere Verweisschlüssel („@clara“); beim Öffnen auf den Hauptschlüssel umgeschrieben und geleert */
  aliases?: string[];
  /** Beziehungen, die von diesem Element ausgehen (relations.ts); fehlend = keine. Die Gegenrichtung steht nur hier, nicht beim anderen. */
  relations?: Relation[];
  /** Position auf der Zeichenfläche der Bibliothek (null: noch nicht angeordnet) */
  x: number | null;
  y: number | null;
  /** Farbplatz des automatischen Bilds (autoImage.ts): fortlaufend je Klasse beim Anlegen vergeben; fehlend = aus der ID */
  colorSlot?: number;
}

/**
 * Material einer Szene (PLAN.md §36): eine Idee bzw. Notiz (`text`), ein Link (`url`) oder ein Bild (`assetId`), jeweils mit Titel. Kein
 * Text der Geschichte – Material zählt für keine Analyse.
 */
export type MaterialItem =
  | { id: string; kind: 'note'; title: string; text: string }
  | { id: string; kind: 'link'; title: string; url: string }
  | { id: string; kind: 'image'; title: string; assetId: string };

/** Textdokument eines Nodes (Markdown), z. B. Zusammenfassung oder Draft. Standarddokumente sind gesperrt: nicht umbenenn- oder löschbar. */
export interface TextDocument {
  id: string;
  name: string;
  /** Markdown; Verweise auf Einträge stehen als `@schlüssel` im Text */
  text: string;
  locked: boolean;
  /** Stellen, die hier bewusst nicht verknüpft werden (Vorkommen samt Nachbarwörtern); fehlend = keine */
  ignored?: IgnoredTerm[];
}

/**
 * Eine bewusst nicht verknüpfte Stelle in einem Textdokument: das Vorkommen mit dem Wort davor und dem danach (leer am Anfang bzw.
 * Ende einer Zeile). Gilt nur in diesem Dokument und nur, solange die Nachbarwörter dieselben sind.
 */
export interface IgnoredTerm {
  term: string;
  before: string;
  after: string;
}

/** Gruppierungsrahmen auf der Zeichenfläche der Bibliothek (z. B. „Hafenstadt“); Einträge innerhalb des Rahmens gehören dazu. */
export interface LibraryGroup {
  id: string;
  /** Bereich der Bibliothek, zu dem die Gruppe gehört (Klasse: Person, Ort, …); sie ist nur dort zu sehen */
  typeId?: string | null;
  name: string;
  color: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Stelle in der Kartenansicht (Sortierschlüssel wie bei Kapiteln); fehlt er, steht die Kategorie hinten, nach Name */
  rank?: string | null;
}

/**
 * Art einer Beziehung (relations.ts): Elternteil (Quelle ist Mutter bzw. Vater des Ziels), Geschwister, Ehe, Liebe (Quelle liebt das
 * Ziel), Freundschaft, Feindschaft, Vorgesetzt (Quelle steht über dem Ziel), Mitglied (Quelle gehört zum Ziel, meist einer Organisation),
 * Weg (zwischen zwei Orten, mit der Reisezeit – für die Prüfung der Reisezeiten, AP-40).
 */
export const RELATION_KINDS = ['elternteil', 'geschwister', 'ehe', 'liebe', 'freundschaft', 'feindschaft', 'vorgesetzt', 'mitglied', 'weg'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

/** Geschlecht, wie es eine Beziehung nennt („Mutter“ = w, „Sohn“ = m) – für die Formen, solange das Element kein Feld „Geschlecht“ hat */
export type RelationForm = 'w' | 'm';

/** Haltung einer Seite zur anderen (AP-30): zugetan, gleichgültig (neutral) oder abgeneigt */
export const ATTITUDES = ['zugetan', 'neutral', 'abgeneigt'] as const;
export type Attitude = (typeof ATTITUDES)[number];

/** Haltung je Richtung: der Quelle zum Ziel (`source`) und des Ziels zur Quelle (`target`); fehlend = nicht angegeben */
export interface RelationAttitude {
  source?: Attitude;
  target?: Attitude;
}

/** Wendepunkt einer Beziehung (AP-30): ab dieser Szene (Weltzeit) gilt diese Haltung, mit einer kurzen Notiz („der Verrat“) */
export interface RelationTurn {
  sceneId: string;
  attitude: RelationAttitude;
  note?: string;
}

/** Beziehung von einem Element (dem, an dem sie steht) zu einem anderen, gültig ab bzw. bis zu einer Szene (fehlend: immer). */
export interface Relation {
  id: string;
  kind: RelationKind;
  targetId: string;
  /** gilt ab dieser Szene (Weltzeit) */
  from?: string | null;
  /** gilt bis zu dieser Szene (einschließlich) */
  to?: string | null;
  /** Geschlecht von Quelle bzw. Ziel, wie es beim Anlegen genannt wurde */
  forms?: { source?: RelationForm; target?: RelationForm };
  /** Haltung je Richtung zu Beginn (AP-30) */
  attitude?: RelationAttitude;
  /** nur Serien (PLAN.md §43): eine Beziehung aus einer früheren Episode – die Nummer der Episode; nie gespeichert */
  inherited?: number;
  /** Wendepunkte: ab einer Szene eine neue Haltung (AP-30) */
  turns?: RelationTurn[];
  /** nur Weg: Reisezeit in Minuten (AP-40) */
  minutes?: number;
}

/**
 * Art einer Zustandsänderung (states.ts): Verfassung einer Figur (geboren, gesund, verletzt, tot), Aufenthaltsort (ein Ort), Besitz (bei
 * wem ein Gegenstand ist – eine Figur, ein Ort oder niemand) und Merkmal (ein Merkmal wie „Haare“ bekommt eine Ausprägung wie „grau“ bzw.
 * fällt weg). Geburt und Tod geben die Lebensdaten (lifespan.ts).
 */
export const STATE_KINDS = ['verfassung', 'ort', 'besitz', 'merkmal'] as const;
export type StateKind = (typeof STATE_KINDS)[number];
/** Höchstlänge von Merkmal und Ausprägung (Zeichen) */
export const TRAIT_MAX = 100;
export const CONDITIONS = ['geboren', 'gesund', 'verletzt', 'tot'] as const;
export type Condition = (typeof CONDITIONS)[number];

/** Zustandsänderung in einer Szene: ab hier gilt für das Element der neue Wert (bis zur nächsten Änderung in Weltzeit). */
export interface StateChange {
  id: string;
  /** wessen Zustand: Figur (Verfassung, Ort) bzw. Gegenstand (Besitz) */
  entityId: string;
  kind: StateKind;
  /**
   * Verfassung: geboren | gesund | verletzt | tot; Ort: der Ort (Eintrag); Besitz: bei wem (Eintrag) bzw. null = verloren, bei niemandem;
   * Merkmal: die Ausprägung (Text) bzw. null = das Merkmal fällt weg
   */
  value: string | null;
  /** nur Merkmal: welches („Haare“, „Narbe“) */
  trait?: string;
  /** nur Besitz: von wem er kommt – wer ihn weitergibt bzw. verliert (Eintrag); fehlend = unbekannt */
  from?: string;
  /** die Stelle im Text, aus der sie stammt (Klartext); fehlend = von Hand */
  quote?: string;
}

export interface DynamicField {
  id: string;
  label: string;
  type: FieldType;
  value: FieldValue;
}

/**
 * Art eines Nodes. `event` und `period` sind die zwei Formen der Szene (Zeitpunkt bzw. Zeitraum, siehe `scene.ts`); `scene` gibt es nur
 * noch in älteren Daten und wird beim Laden übernommen (`migrateScenes`).
 */
export type NodeKind = 'story' | 'timeline' | 'event' | 'period' | 'instance' | 'normal' | 'manuscript' | 'section' | 'chapter' | 'scene';

/**
 * Wie eine Szene ein Ereignis erzählt: `shows` szenisch (Erzählgegenwart), `flashback` als Rückblende, `flashforward` als
 * Vorausdeutung, `mentions` nur erwähnt oder zusammengefasst.
 */
export const NARRATE_MODES = ['shows', 'flashback', 'flashforward', 'mentions'] as const;
export type NarrateMode = (typeof NARRATE_MODES)[number];

/** Eintrag der Erzählt-Liste einer Szene: welches Ereignis bzw. welcher Zeitraum auf welche Art erzählt wird. */
export interface Narration {
  nodeId: string;
  mode: NarrateMode;
}

/** Zeitfähnchen: die Beschriftung ist die Zeit an seiner Position; ein manuell gesetzter Text (`label`) ersetzt sie. */
export interface Flag {
  id: string;
  pos: number;
  /** Manueller Text; leer = automatische Zeitanzeige */
  label: string;
  /** Abstand zum Zeitstrahl auf der y-Achse (negativ = darüber, positiv = darunter); 0 oder fehlend = direkt auf dem Zeitstrahl */
  dy?: number;
}

/** Welche Teile der Zeit auf dem Zeitstrahl angezeigt werden (Zeitanzeige in den Einstellungen des Zeitstrahls). */
export interface TimeDisplay {
  year: boolean;
  month: boolean;
  day: boolean;
  time: boolean;
}

/**
 * Zeitstrahl: Positionen sind visuelle Achsenwerte, unabhängig von den Datumsbeschriftungen der Fähnchen.
 * `startPos`/`endPos` wachsen automatisch mit den Elementen und lassen sich an den Enden manuell verlängern.
 */
/** Einheiten einer eigenen Zählung */
export const TIME_UNITS = ['minutes', 'hours', 'days', 'years'] as const;
export type TimeUnit = (typeof TIME_UNITS)[number];

/**
 * Eigene Zählung eines Zeitstrahls statt Datum und Uhrzeit: Start und Ende als Zahl in einer Einheit (z. B. Tag 0 bis Tag 30).
 * Mit Nullpunkt (Datum) lässt er sich an datierte Zeitstrahle koppeln und mit Datumsangaben vergleichen.
 */
export interface Reckoning {
  unit: TimeUnit;
  start: number;
  end: number;
  /** Datum und Uhrzeit des Nullpunkts (`JJJJ-MM-TTThh:mm`); fehlt er, zählt der Zeitstrahl für sich */
  zero?: string | null;
}

/** Ein Monat eines eigenen Kalenders */
export interface CalendarMonth {
  name: string;
  /** Tage (in Schaltjahren einer mehr, wenn er der Schaltmonat ist) */
  days: number;
}

/**
 * Eigener Kalender einer Geschichte (PF 16, calendar.ts): Monate mit Namen und Länge, Wochentage, die Jahreszählung (Zusatz hinter der
 * Jahreszahl) und optional ein Schalttag. Gilt er, sind alle Daten der Geschichte (`JJJJ-MM-TTThh:mm`) Daten dieses Kalenders: Monat 3
 * ist sein dritter Monat. Tage haben 24 Stunden zu 60 Minuten.
 */
export interface Calendar {
  name: string;
  months: CalendarMonth[];
  /** Namen der Wochentage in ihrer Reihenfolge; leer = keine Woche */
  weekdays: string[];
  /** Wochentag (Index in `weekdays`) des ersten Tags im Jahr 1 */
  firstWeekday: number;
  /** Zusatz hinter der Jahreszahl („n. F.“, „DZ“); leer = keiner */
  era: string;
  /** Schaltjahr: alle `every` Jahre (Jahreszahl teilbar durch `every`) hat der Monat `month` (1 = erster) einen Tag mehr */
  leap?: { every: number; month: number } | null;
}

export interface TimelineData {
  startPos: number;
  endPos: number;
  /** Datum und Uhrzeit an Start und Ende (`JJJJ-MM-TTThh:mm`), sofern keine eigene Zählung gilt */
  startLabel: string;
  endLabel: string;
  flags: Flag[];
  /** Zeitanzeige; fehlt sie, werden alle Teile angezeigt */
  display?: TimeDisplay;
  /** Eigene Zählung; fehlt sie, gelten Datum und Uhrzeit aus `startLabel`/`endLabel` */
  reckoning?: Reckoning | null;
  /** An die Hauptzeitlinie gekoppelt: Lage und Maßstab folgen ihr (nur bei weiteren Zeitstrahlen, siehe `alignToMain`) */
  coupled?: boolean;
}

/** Genauigkeit einer Zeitangabe: bis zu welcher Stelle sie gilt („1823“ = Jahr, „im März 1823“ = Monat, „am 3. März gegen acht“ = Stunde) */
export const TIME_PRECISIONS = ['year', 'month', 'day', 'hour', 'minute'] as const;
export type TimePrecision = (typeof TIME_PRECISIONS)[number];

/** Woher die Zeit einer Szene stammt: aus dem Text (Zeitmarke), von der Zeitlinie (Lage) oder von Hand (Seitenleiste, Anlegen) */
export const TIME_SOURCES = ['text', 'timeline', 'manual'] as const;
export type TimeSource = (typeof TIME_SOURCES)[number];

/**
 * Zeit einer Szene in der Welt (PLAN.md §15, „Zeit zuerst“): die Angabe gehört der Szene, nicht ihrer Lage auf einer Fläche. Werte als
 * Text wie bisher – Datum `JJJJ-MM-TTThh:mm` oder eine eigene Zählung („Tag 12, 06:00“). Angedockt an einen Zeitstrahl mit Zeitrechnung
 * folgt die Lage aus der Zeit.
 */
export interface SceneTime {
  /** Zeitpunkt bzw. Beginn; null = unbekannt */
  start: string | null;
  /** Ende; mit Ende ist die Szene ein Zeitraum, ohne ein Zeitpunkt (die Form folgt der Zeit, PLAN.md §17) */
  end: string | null;
  /** Dauer statt eines Endes (`3d`, `2h`, `45min`, `1w`, `2mo`, `1y`): das Ende ist dann Beginn + Dauer */
  duration?: string;
  /** Wie genau die Angabe ist; fehlend = so genau, wie der Wert ist (Minute) */
  precision?: TimePrecision;
  /** Ungefähr („gegen acht“, „im Frühling“) */
  approx?: boolean;
  /** Woher die Zeit stammt; fehlend = von Hand */
  source?: TimeSource;
  /**
   * Relativ zu einer anderen Szene (PLAN.md §16): `scene` ist die Ankerszene, von deren Zeit (Ende bzw. Beginn) relative Angaben ausgehen.
   * Aus dem Text („drei Tage später“) kommt der Abstand aus den Zeitmarken; von Hand steht er in `offset` (Wert wie eine relative
   * Zeitmarke, z. B. `+3d`). Verschiebt sich die Ankerszene, ziehen abhängige Szenen nach.
   */
  anchor?: { scene: string; offset?: string };
}

export interface StoryNode {
  id: string;
  /** Übergeordneter Node (null nur bei der Story). Elemente eines Zeitstrahls haben den Zeitstrahl als Eltern. */
  parentId: string | null;
  typeId: string;
  kind: NodeKind;
  /** Bei Instanzen: der Bibliothekseintrag, dessen statische Felder gelten. */
  entityId: string | null;
  /** Eigene Feldwerte (normale Nodes, Ereignisse, Zeiträume, Zeitstrahl, Story). Bei Instanzen ungenutzt. */
  fields: Record<string, FieldValue>;
  dynamicFields: DynamicField[];
  /** Textdokumente (nur Nodes von Typen mit `textual`: Szenen mit ihrem Draft, eigene Klassen mit Häkchen, die Geschichte) */
  documents: TextDocument[];
  /** Frei wählbare zweite Farbmarkierung */
  tagColor: string | null;
  /** Angedocktes Ereignis bzw. angedockter Zeitraum: Abstand der Beschriftungsbox zum Element auf der y-Achse (null/fehlend = Standardabstand darunter) */
  labelDy?: number | null;
  /**
   * Vorschaukarte eines angedockten Zeitpunkts bzw. Zeitraums: was sie zeigt (Zeit, Inhalte, Kurzbeschreibung; fehlend = Zeit und
   * Inhalte an, Kurzbeschreibung aus) und ob sie eingeklappt ist. `image` stammt aus einer früheren Version und wird nicht mehr genutzt.
   */
  labelShow?: { time?: boolean; content?: boolean; description?: boolean; image?: boolean } | null;
  /**
   * Position auf dem Canvas des Elternnodes. Am Zeitstrahl angedockte Ereignisse und Zeiträume haben keine freie Position (`null`):
   * ihre Lage ergibt sich aus `at` bzw. `from`/`to`.
   */
  x: number | null;
  y: number | null;
  /** Notizzettel: Größe auf der Fläche (fehlend = Standardgröße, siehe `NOTE_SIZE`) */
  w?: number | null;
  h?: number | null;
  /** Ereignis: Position auf dem Zeitstrahl (hat er eine Zeitrechnung, folgt sie aus `time`) */
  at: number | null;
  /** Zeitraum: Bereich auf dem Zeitstrahl (hat er eine Zeitrechnung, folgt er aus `time`) */
  from: number | null;
  to: number | null;
  timeline: TimelineData | null;
  /** Wurzel der Geschichte: ihr eigener Kalender (PF 16); fehlend bzw. null = der gregorianische */
  calendar?: Calendar | null;
  /**
   * Szene: ihre Zeit in der Welt (null bzw. fehlend = keine). Ältere Daten (Zeit aus der Lage bzw. aus früheren Zeitfeldern) werden beim
   * Laden übernommen (`migrateSceneTimes`). Andere Nodes haben keine.
   */
  time?: SceneTime | null;
  /** Kapitel: Stelle im Manuskript; Szene: Stelle in ihrem Kapitel (siehe `rankBetween`); sonst null bzw. fehlend */
  rank?: string | null;
  /** Szene: Kapitel im Manuskript (null bzw. fehlend = nicht eingeordnet) */
  chapterId?: string | null;
  /** nur ältere Daten: welche Ereignisse eine Szene erzählte (wird beim Laden aufgelöst, siehe `migrateScenes`) */
  narrates?: Narration[];
  /** Szene ohne Kapitel: bewusst nicht erzählt (Ellipse, Vorgeschichte); der Hinweis „nicht eingeordnet“ entfällt */
  narrateSkip?: boolean;
  /** Szene: was sich in ihr ändert (Verfassung, Ort, Besitz; states.ts); fehlend = nichts */
  changes?: StateChange[];
  /** Szene: gesammeltes Material – Ideen, Links, Bilder (material.ts); zählt für keine Analyse; fehlend = nichts */
  material?: MaterialItem[];
  /**
   * Kapitel: Farbplatz der automatischen Bilder seiner Szenen (autoImage.ts), fortlaufend beim Anlegen vergeben; fehlend = aus der ID.
   * Ältere Szenen tragen noch einen eigenen (A130), er wird nicht mehr benutzt.
   */
  colorSlot?: number;
  /** Manuskript: seine Beat-Listen (beats.ts, PLAN.md §42); fehlend = keine */
  beatSheets?: BeatSheet[];
  /** Szene: ihre Wertverschiebung von −2 bis +2 (beats.ts, PLAN.md §42); fehlend bzw. null = nicht angegeben */
  shift?: number | null;
  createdAt: string;
}

/**
 * Story Beat (PLAN.md §42): eine Station der Dramaturgie auf der Lesereihenfolge – was an dieser Stelle der Erzählung geschehen soll.
 * Die Szenen tragen ihn; ohne Szene ist er offen.
 */
export interface Beat {
  id: string;
  title: string;
  /** was hier geschehen soll (aus der Vorlage, änderbar) */
  description?: string;
  /** Soll-Stelle als Anteil der Lesereihenfolge (0 = Anfang, 1 = Ende); fehlend = keine */
  target?: number;
  /** hier beginnt ein neuer Akt */
  act?: boolean;
  /** die Szenen, die den Beat tragen; leer = offen */
  sceneIds: string[];
}

/** Beat-Liste am Manuskript: der Hauptplot, ein Nebenplot oder der Bogen einer Figur */
export interface BeatSheet {
  id: string;
  title: string;
  /** Vorlage, aus der die Liste entstand (`BEAT_TEMPLATES`); fehlend = eigene */
  template?: string;
  /** Figur, deren Bogen die Liste beschreibt; fehlend bzw. null = die ganze Geschichte */
  entityId?: string | null;
  beats: Beat[];
}

/** Wertverschiebung einer Szene: stark abwärts bis stark aufwärts */
export const SHIFTS = [-2, -1, 0, 1, 2] as const;

export interface Connection {
  id: string;
  sourceId: string;
  targetId: string;
  /** Wird im Canvas gezeichnet oder nicht */
  visible: boolean;
  label: string;
}

export interface Story {
  rootId: string;
  types: NodeType[];
  entities: Entity[];
  /** Gruppierungsrahmen der Bibliothek */
  groups: LibraryGroup[];
  nodes: StoryNode[];
  connections: Connection[];
  /**
   * Die Geschichte als Episode einer Serie (PLAN.md §43, series.ts): ihre Stelle und die Vorgeschichte aus den früheren Episoden – was
   * dort über Zustände, Beziehungen und Wissen feststeht. Berechnet beim Öffnen, nie gespeichert; fehlend = keine Serie mit Episoden davor.
   */
  series?: SeriesContext | null;
}

/** Woher ein Stand der Vorgeschichte kommt: die frühere Episode (Nummer, 1 = erste) und der Titel der Szene dort */
export interface PriorSource {
  episode: number;
  scene: string;
}

/** Geburt bzw. Tod in einer früheren Episode, mit der Zeit der Szene (für Alter und Vergleich in Weltzeit) */
export interface PriorLife extends PriorSource {
  span: { basis: string; start: number; end: number } | null;
  approx: boolean;
}

/** Ein Element der Bibliothek, das auch in früheren Episoden steht, und sein Stand dort am Ende (IDs dieser Geschichte) */
export interface PriorEntity {
  entityId: string;
  condition?: PriorSource & { value: Condition };
  place?: PriorSource & { value: string };
  /** Gegenstand: bei wem er ist (null: bei niemandem) */
  holder?: PriorSource & { value: string | null };
  traits?: (PriorSource & { trait: string; value: string })[];
  born?: PriorLife;
  died?: PriorLife;
}

/** Eine Beziehung, die am Ende der vorigen Episode gilt (ohne „ab“/„bis“, die Haltung die am Ende; `inherited`: ihre Episode) */
export interface PriorRelation {
  sourceId: string;
  relation: Relation & { inherited: number };
}

/** Ein Wissen aus früheren Episoden: wer (Figur bzw. null = der Leser) weiß was (Eintrag) */
export interface PriorKnown extends PriorSource {
  knower: string | null;
  about: string;
  text: string;
}

/**
 * Ein Element, das in mehreren Episoden der Serie steht: `origin` ist die Episode des Originals (wo es entstand) – ist es nicht diese, ist
 * das Element hier eine Referenz; `episodes` sind alle Episoden, in denen es steht
 */
export interface SeriesLink {
  entityId: string;
  origin: number;
  episodes: number[];
}

/** Stelle einer Geschichte in ihrer Serie und ihre Vorgeschichte */
export interface SeriesContext {
  id: string;
  name: string;
  /** Nummer dieser Episode (1 = erste) und Zahl der Episoden der Serie */
  number: number;
  count: number;
  /** alle Episoden der Serie, in ihrer Reihenfolge */
  episodes: { id: string; name: string; number: number }[];
  /** Elemente dieser Episode, die auch in anderen stehen (Referenzen und Originale mit Referenzen) */
  links: SeriesLink[];
  entities: PriorEntity[];
  relations: PriorRelation[];
  knowledge: PriorKnown[];
}

/** Aufbereitete Anzeigedaten eines Nodes. */
export interface Display {
  title: string;
  hover: string;
  imageId: string | null;
  typeName: string;
  typeColor: string;
  tagColor: string | null;
  /** Szene in einem Kapitel: die (blasse) Farbe des Kapitels (autoImage `chapterColorOf`), sonst null */
  chapterColor: string | null;
  category: TypeCategory;
  /** Leere Nodes werden immer als Kreis dargestellt */
  empty: boolean;
  /** Verweisschlüssel des Eintrags (nur Instanzen) */
  refKey: string | null;
}
